import { UnitSystem, Currency } from '../types';
import { SLAB_SPECIFICATIONS } from '../data/customization';

export const convertLength = (value: number, from: UnitSystem, to: UnitSystem): number => {
  if (from === to) return value;
  
  // Convert from metric to imperial
  if (from === 'metric' && to === 'imperial') {
    return value / 25.4; // Convert mm to inches
  }
  
  // Convert from imperial to metric
  return value * 25.4; // Convert inches to mm
};

export const convertArea = (value: number, from: UnitSystem, to: UnitSystem): number => {
  if (from === to) return value;
  
  // Convert from metric to imperial
  if (from === 'metric' && to === 'imperial') {
    return value * 10.764; // Convert m² to sq ft
  }
  
  // Convert from imperial to metric
  return value / 10.764; // Convert sq ft to m²
};

export const formatCurrency = (
  amount: number,
  currency: Currency = 'EUR',
  locale: string = 'en-US'
): string => {
  // Ensure amount is a valid number
  if (typeof amount !== 'number' || isNaN(amount)) {
    amount = 0;
  }

  // Round to 2 decimal places to avoid floating point issues
  amount = Math.round(amount * 100) / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const calculateArea = (width: number, length: number): number => {
  return (width * length) / 1000000; // Convert mm² to m²
};

export const calculateSlabsNeeded = (area: number): number => {
  // Always round up to the nearest whole slab, minimum 1 slab
  return Math.max(1, Math.ceil(area / SLAB_SPECIFICATIONS.standardArea));
};

export const calculateMaterialCost = (area: number, slabPrice: number): number => {
  // Calculate slabs needed (minimum 1)
  const slabsNeeded = calculateSlabsNeeded(area);
  // Return total cost based on number of slabs needed
  return slabsNeeded * slabPrice;
};

// Utility function to format dimensions with both mm and cm
export const formatDimensionWithUnits = (value: number): string => {
  const mm = Math.round(value);
  const cm = (value / 10).toFixed(1);
  return `${mm}mm (${cm}cm)`;
};

// Utility function to format area with both m² and cm²
export const formatAreaWithUnits = (value: number): string => {
  const m2 = value.toFixed(2);
  const cm2 = (value * 10000).toFixed(0);
  return `${m2}m² (${cm2}cm²)`;
};

// Calculate price per unit area
export const calculatePricePerUnit = (totalPrice: number, area: number): number => {
  if (area <= 0) return 0;
  return totalPrice / area;
};

// Format percentage with specified precision
export const formatPercentage = (value: number, precision: number = 1): string => {
  return `${value.toFixed(precision)}%`;
};

// Calculate volume in cubic meters
export const calculateVolume = (width: number, length: number, thickness: number): number => {
  return (width * length * thickness) / 1000000000; // Convert mm³ to m³
};

// Format weight in kg
export const formatWeight = (weight: number, locale: string = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(weight) + ' kg';
};

// Calculate shipping weight including packaging
export const calculateShippingWeight = (weight: number): number => {
  const packagingWeight = weight * 0.1; // 10% additional for packaging
  return weight + packagingWeight;
};

// Format dimensions for display
export const formatDimensions = (width: number, length: number, thickness: number): string => {
  return `${formatDimensionWithUnits(width)} × ${formatDimensionWithUnits(length)} × ${thickness}mm`;
};